import { Box, styled } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Retailer } from "@/redux/types";
import LeavingConfirm from "./LeavingConfirm";
import { RetailerConnectBenefits } from "./RetailerConnectBenefits";
import { RetailerConnectLogin } from "./RetailerConnectLogin";
import { ResponsiveDialog } from "@brandclub/common-ui";
import { create } from "zustand";

export enum PopupStatus {
  WELCOME = "welcome",
  CONNECT = "connect",
  LEAVING = "leaving",
}

interface DialogState {
  connectReward: number;
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  currentUsername: string;
  currentPassword: string;
  checkoutIntentUsername?: string;
  retailer?: Retailer;
  defaultStatus?: PopupStatus;
  hideLeaving?: boolean;
  customSubtitle?: string;
  errorBorder?: boolean;
  guestCheckoutClick?: () => void;
}

const defaultState: DialogState = {
  connectReward: 0,
  onSaveCredentials: async () => {},
  currentUsername: "",
  currentPassword: "",
  checkoutIntentUsername: "",
  retailer: undefined,
  defaultStatus: PopupStatus.WELCOME,
  hideLeaving: false,
  customSubtitle: "",
  errorBorder: false,
  guestCheckoutClick: () => {},
};

const useDialogStore = create<
  DialogState & { open: boolean; close: () => void }
>((set) => ({
  ...defaultState,
  open: false,
  close: () => set({ open: false }),
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",

  flexDirection: "column",

  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.vars.palette.text.primary,
  ".retailer-connect-login-motion-container": {
    padding: "34px 24px 55px 24px",
    [theme.breakpoints.down("md")]: {
      padding: "34px 20px 22px 20px",
    },
  },
}));

const EditConnectPopup = () => {
  const {
    open,
    close,
    connectReward,
    onSaveCredentials,
    currentUsername,
    currentPassword,
    retailer,
    checkoutIntentUsername,
    defaultStatus = PopupStatus.WELCOME,
    hideLeaving = false,
    errorBorder = false,
    customSubtitle,
  } = useDialogStore();
  const [status, setStatus] = useState<PopupStatus>(defaultStatus);
  const [previousStatus, setPreviousStatus] =
    useState<PopupStatus>(defaultStatus);
  const [shouldConfirmLeave, setShouldConfirmLeave] = useState(true);

  useEffect(() => {
    if (
      !checkoutIntentUsername ||
      !currentUsername ||
      currentUsername === "" ||
      !currentPassword ||
      currentPassword === ""
    ) {
      setShouldConfirmLeave(true);
    } else {
      setShouldConfirmLeave(false);
    }
  }, [checkoutIntentUsername, open, currentUsername, currentPassword]);

  const onCloseEdit = () => {
    setStatus(defaultStatus);
    setShouldConfirmLeave(false);
    close();
  };

  const getDialogBody = () => {
    switch (status) {
      case PopupStatus.WELCOME:
        return (
          <Box
            sx={(theme) => ({
              width: "100%",
              height: "100%",
            })}
          >
            <RetailerConnectBenefits
              rewardAmount={connectReward}
              retailerId={retailer?.retailerId}
              onContinue={() => {
                setStatus(PopupStatus.CONNECT);
              }}
            />
          </Box>
        );
      case PopupStatus.CONNECT:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <RetailerConnectLogin
              errorBorder={errorBorder}
              closeEdit={onCloseEdit}
              onSaveCredentials={onSaveCredentials}
              customSubtitle={customSubtitle}
              onChange={(event) => {
                if (
                  event.edited ||
                  !currentUsername ||
                  currentUsername === "" ||
                  !currentPassword ||
                  currentPassword === "" ||
                  event.username !== currentUsername ||
                  event.password !== currentPassword
                ) {
                  setShouldConfirmLeave(true);
                }
              }}
              currentUsername={currentUsername}
              currentPassword={currentPassword}
              retailer={retailer}
            />
          </Box>
        );
      case PopupStatus.LEAVING:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <LeavingConfirm
              closeEdit={onCloseEdit}
              goBack={() => {
                setStatus(previousStatus);
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={
        shouldConfirmLeave && !hideLeaving
          ? () => {
              setPreviousStatus(status);
              setStatus(PopupStatus.LEAVING);
            }
          : onCloseEdit
      }
    >
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            width: 580,
          },
          overflowY: "auto",
          maxWidth: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",

              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            {getDialogBody()}
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};

EditConnectPopup.show = (props: DialogState) => {
  useDialogStore.setState({
    ...props,
    open: true,
  });
};

EditConnectPopup.close = () => {
  useDialogStore.setState({
    ...defaultState,
    open: false,
  });
};

export default EditConnectPopup;
